import { useEffect, useState } from 'react';
import { TFunction } from 'i18next';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '../../../componentsv2/Table';
import Typography from '../../../componentsv2/Typography';
import Chip from '../../../componentsv2/Chip';
import InfoDrawer from '../../../componentsv2/InfoDrawer';
import DetailsSection from '../../../componentsv2/DetailsSection';
import Button from '../../../componentsv2/Button';
import { DownloadIcon } from '../../../components/Icons';
import { adminDisableGlobalAccount } from '../../../services/admin';
import { useSnackbar } from '../../../context';
import ConfirmDialog from '../../../componentsv2/ConfirmDialog';
import { defaultStatusColor, statusColorMap } from '../../../constants/banking';
import { BankingCurrency, GlobalAccount, GlobalAccountStatus } from '../../../types/banking';
import { getRoutingCodesFromGlobalAccount, getTransferMethodsForCurrency } from '../../../utils/banking';
import TransferMethods from './TransferMethods';
import { useExportGlobalAccountStatement } from '../../../hooks/useAdminBanking';

function getIntermediaryBank(routingCodes: { type: string; value: string }[], swiftCode: string) {
  // for specific SXPYDKKK swift code, return citibank
  if (swiftCode === 'SXPYDKKK') {
    return { name: 'Citibank N.A.', swiftCode: 'CITIGB2L' };
  }

  // else, use the ACH routing code
  const ach = routingCodes.find(({ type }) => type === 'ach')?.value;
  if (!ach || !ach.endsWith('6768')) {
    return null;
  }

  return {
    currency: 'USD',
    name: "First National Banker's Bank",
    swiftCode: 'FRNAUS44',
    address: '7813 Office Park Blvd',
    city: 'Baton Rouge, LA',
    zipCode: '70809',
  };
}

export const GlobalAccountDetails = ({
  t,
  globalAccount,
  marcoPayNumber,
  defaultCurrency,
  showTitle = true,
  trackingEvent,
  accountId,
  showExportStatementButton = false,
}: {
  t: TFunction;
  globalAccount: GlobalAccount;
  marcoPayNumber?: string;
  defaultCurrency?: BankingCurrency;
  showTitle?: boolean;
  trackingEvent?: string;
  accountId?: number;
  showExportStatementButton?: boolean;
}) => {
  const [selectedCurrency, setSelectedCurrency] = useState(defaultCurrency ?? globalAccount.mainCurrency);
  const routingCodes = getRoutingCodesFromGlobalAccount(globalAccount, selectedCurrency);
  const transferMethods = getTransferMethodsForCurrency(globalAccount, selectedCurrency);
  const { showSnackbar } = useSnackbar();
  const exportStatementMutation = useExportGlobalAccountStatement({ accountId });

  const handleDownloadStatement = () => {
    if (!accountId) return;

    exportStatementMutation.mutate(
      { globalAccountId: globalAccount.globalAccountId },
      {
        onSuccess: (blob) => {
          const url = window.URL.createObjectURL(new Blob([blob]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `statement-${globalAccount.accountNumber}.pdf`);
          document.body.appendChild(link);
          link.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(link);
        },
        onError: () => {
          showSnackbar(t('common:errorBoundary.title'), 'error');
        },
      }
    );
  };

  const getAccountIdentifierDetails = (globalAccount: GlobalAccount) => {
    // Mexican accounts use CLABE
    if (globalAccount.countryCode === 'MX') {
      return [
        {
          label: t('banking:connectedAccountDetails.detailsDrawer.accountDetails.clabe'),
          value: globalAccount.accountNumber,
          copyable: true,
        },
      ];
    }

    // Accounts with IBAN
    if (globalAccount.iban) {
      return [
        {
          label: t('banking:connectedAccountDetails.detailsDrawer.accountDetails.iban'),
          value: globalAccount.iban,
          copyable: true,
        },
      ];
    }

    // Standard bank account number
    return [
      {
        label: t('banking:connectedAccountDetails.detailsDrawer.accountDetails.bankAccountNumber'),
        value: globalAccount.accountNumber,
        copyable: true,
      },
    ];
  };

  return (
    <Grid container>
      {showTitle && (
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="h5" fontWeight="700">
              {globalAccount.accountName}
            </Typography>
            <Chip
              label={t(`banking:connectedAccounts.columns.status.values.${globalAccount.status}`)}
              sx={{
                marginLeft: '1em',
                ...(statusColorMap[globalAccount.status] || defaultStatusColor),
              }}
            />
          </Box>
          <Typography>
            {t(`banking:connectedAccountDetails.detailsDrawer.description`, {
              currency: selectedCurrency,
            })}
          </Typography>
        </Grid>
      )}
      {globalAccount.currencies.length > 1 && (
        <Grid item xs={12} sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginY: '12px' }}>
          {globalAccount.currencies.map((currency) => (
            <Button
              key={currency}
              type={currency === selectedCurrency ? 'fill' : 'outline'}
              color="secondary"
              size="small"
              onClick={() => setSelectedCurrency(currency)}
              sx={{ minHeight: 'auto', paddingY: '4px', paddingX: '12px' }}
            >
              {currency}
            </Button>
          ))}
        </Grid>
      )}
      {transferMethods.length > 0 && (
        <Grid item xs={12}>
          <TransferMethods t={t} supportedFeatures={transferMethods} swiftCode={globalAccount.swiftCode} />
        </Grid>
      )}
      <Grid item xs={12}>
        <DetailsSection
          trackingEvent={trackingEvent}
          key={`account-details-${globalAccount.accountNumber}`} // Unique key based on account number
          title={t('banking:connectedAccountDetails.detailsDrawer.accountDetails.title')}
          endAction={
            showExportStatementButton ? (
              <Button
                type="link"
                color="secondary"
                startIcon={
                  !exportStatementMutation.isLoading ? <DownloadIcon /> : <CircularProgress size={16} />
                }
                disabled={exportStatementMutation.isLoading}
                onClick={handleDownloadStatement}
                size="small"
              >
                {t('banking:connectedAccountDetails.detailsDrawer.downloadStatement')}
              </Button>
            ) : undefined
          }
          details={[
            {
              label: t('banking:connectedAccountDetails.detailsDrawer.accountDetails.accountName'),
              value: globalAccount.accountName,
              copyable: true,
            },
            ...getAccountIdentifierDetails(globalAccount),
            ...routingCodes?.map(({ type, value }) => ({
              label: t(`banking:connectedAccountDetails.detailsDrawer.accountDetails.${type}`),
              value,
              copyable: true,
            })),
            // Marco Pay number (wallet proxy ID) — account-level, shown here for convenience
            ...(marcoPayNumber
              ? [
                  {
                    label: t('banking:connectedAccountDetails.detailsDrawer.accountDetails.marcoPayNumber'),
                    value: marcoPayNumber,
                    copyable: true,
                  },
                ]
              : []),
            // Only add SWIFT code details if not present in routing codes
            ...(!routingCodes.find(({ type }) => type === 'swift')
              ? [
                  {
                    label: t('banking:connectedAccountDetails.detailsDrawer.accountDetails.swift'),
                    value: globalAccount.swiftCode || '-',
                    copyable: true,
                  },
                ]
              : []),
            {
              label: t('banking:connectedAccountDetails.detailsDrawer.accountDetails.bankName'),
              value: globalAccount.institution?.name ?? '',
              copyable: true,
            },
            {
              label: t('banking:connectedAccountDetails.detailsDrawer.accountDetails.accountLocation'),
              value: t(
                `banking:connectedAccountDetails.globalAccounts.columns.bankLocation.countryCodes.${globalAccount.countryCode}`
              ),
            },
            ...(globalAccount.accountType
              ? [
                  {
                    label: t('banking:connectedAccountDetails.detailsDrawer.accountDetails.accountType'),
                    value: globalAccount.accountType,
                  },
                ]
              : []),
            {
              label: t('banking:connectedAccountDetails.detailsDrawer.accountDetails.bankAddress'),
              value: globalAccount.institution?.address ?? '',
              copyable: true,
            },
            {
              label: t('banking:connectedAccountDetails.detailsDrawer.accountDetails.city'),
              value: globalAccount.institution?.city ?? '',
            },
            {
              label: t('banking:connectedAccountDetails.detailsDrawer.accountDetails.zipCode'),
              value: globalAccount.institution?.zipCode || '-',
            },
          ]}
        />
      </Grid>
      <Grid item xs={12}>
        <BanksDetails
          key={`bank-details-${globalAccount.accountNumber}`} // Unique key based on account number
          currency={selectedCurrency}
          swiftCode={globalAccount.swiftCode}
          routingCodes={routingCodes}
          t={t}
        />
      </Grid>
    </Grid>
  );
};

const correspondentBankMapping: Record<string, Record<string, object>> = {
  SCBLHKHH: {
    USD: { name: 'Standard Chartered Bank', swiftCode: 'SCBLUS33' },
    EUR: { name: 'Standard Chartered Bank', swiftCode: 'SCBLDEFX' },
    AUD: { name: 'Australia and New Zealand Banking Group Limited', swiftCode: 'ANZBAU3M' },
    CAD: { name: 'Royal Bank of Canada', swiftCode: 'ROYCCAT2' },
    CHF: { name: 'Credit Suisse', swiftCode: 'CRESCHZZ80A' },
    GBP: { name: 'Credit Suisse (Schweiz) AG', swiftCode: 'SCBLGB2L' },
    JPY: { name: 'Standard Chartered Bank', swiftCode: 'SCBLJPJT' },
    NZD: { name: 'ASB Bank Limited', swiftCode: 'ASBBNZ2A' },
    SGD: { name: 'Standard Chartered Bank (Singapore) Limited', swiftCode: 'SCBLSG22' },
  },
  DHBKHKHH: {
    USD: { name: 'JP Morgan Chase Bank', swiftCode: 'CHASUS33' },
    EUR: { name: 'Barclays Bank Ireland PLC', swiftCode: 'BARCDEFF' },
    AUD: { name: 'Commonwealth Bank of Australia', swiftCode: 'CTBAAU2S' },
    CAD: { name: 'Toronto-Dominion Bank', swiftCode: 'TDOMCATTTOR' },
    CHF: { name: 'UBS AG', swiftCode: 'UBSWCHZH80A' },
    GBP: { name: 'National Westminster Bank PLC', swiftCode: 'NWBKGB2L' },
    JPY: { name: 'Sumitomo Mitsui Banking Corporation', swiftCode: 'SMBCJPJT' },
    NZD: { name: 'ASB Bank Limited', swiftCode: 'ASBBNZ2A' },
    SGD: { name: 'DBS Bank Ltd', swiftCode: 'DBSSSGSG' },
  },
  SXPYDKKK: {
    CHF: {
      name: 'UBS AG',
      swiftCode: 'UBSWCHZH80A',
    },
    CZK: {
      name: 'Citibank Europe Plc.',
      swiftCode: 'CITICZPX',
    },
    HUF: {
      name: 'Citibank Europe Plc.',
      swiftCode: 'CITIHUHX',
    },
    MXN: {
      name: 'Banco Nacional De Mexico',
      swiftCode: 'BNMXMXMMCVT',
    },
    NOK: {
      name: 'Den Norske Bank',
      swiftCode: 'DNBANOKK',
    },
    PLN: {
      name: 'Bank Handlowy',
      swiftCode: 'CITIPLPX',
    },
    RON: {
      name: 'Citibank Romania',
      swiftCode: 'CITIROBU',
    },
    SEK: {
      name: 'Svenska Handelsbanken',
      swiftCode: 'HANDSESS',
    },
    ZAR: {
      name: 'Citibank N.A.',
      swiftCode: 'CITIZAJX',
    },
    ILS: {
      name: 'Citibank N.A.',
      swiftCode: 'CITIILIT',
    },
  },
};

function BanksDetails({
  routingCodes,
  currency,
  swiftCode,
  t,
}: {
  routingCodes: { type: string; value: string }[];
  currency: string;
  swiftCode: string;
  t: TFunction;
}) {
  const correspondentBank = correspondentBankMapping[swiftCode]?.[currency];
  const intermediaryBank = getIntermediaryBank(routingCodes, swiftCode);
  return (
    <Grid container>
      {correspondentBank && (
        <Grid item xs={12}>
          <DetailsSection
            title={t('banking:connectedAccountDetails.detailsDrawer.correspondentBank.title')}
            details={Object.keys(correspondentBank).map((key) => ({
              label: t(`banking:connectedAccountDetails.detailsDrawer.correspondentBank.${key}`),
              value: correspondentBank[key as keyof typeof correspondentBank],
            }))}
          />
        </Grid>
      )}
      {intermediaryBank && (
        <Grid item xs={12}>
          <DetailsSection
            title={t('banking:connectedAccountDetails.detailsDrawer.intermediaryBank.title')}
            details={Object.keys(intermediaryBank).map((key) => ({
              label: t(`banking:connectedAccountDetails.detailsDrawer.intermediaryBank.${key}`),
              value: intermediaryBank[key as keyof typeof intermediaryBank] as string,
            }))}
          />
        </Grid>
      )}
    </Grid>
  );
}

const GlobalAccountsTable = ({
  t,
  globalAccounts,
  marcoPayNumber,
  accountId,
  refreshBankingData,
  fetchGlobalAccountDetail,
  showNickname = true,
  showEnableButton = true,
}: {
  t: TFunction;
  globalAccounts: GlobalAccount[];
  marcoPayNumber?: string;
  // Kept for backwards compatibility with callers that still pass it; the table
  // no longer gates clickability on company — it uses fetchGlobalAccountDetail.
  companyId?: number;
  accountId: number;
  refreshBankingData: () => Promise<void>;
  // Injected by the container so the drawer refreshes the GA from Airwallex on
  // open. Reusable component never calls the backend itself.
  fetchGlobalAccountDetail?: (id: number) => Promise<GlobalAccount>;
  showNickname?: boolean;
  showEnableButton?: boolean;
}) => {
  const [selectedAccount, setSelectedAccount] = useState<GlobalAccount | null>(null);
  const [refreshedAccount, setRefreshedAccount] = useState<GlobalAccount | null>(null);
  const [isTogglingAccount, setIsTogglingAccount] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const { showSnackbar } = useSnackbar();

  // On drawer open, re-fetch the GA detail from Airwallex (if the container
  // provided a fetcher) so the drawer always shows the freshest data.
  useEffect(() => {
    if (!selectedAccount || !fetchGlobalAccountDetail) return;
    fetchGlobalAccountDetail(selectedAccount.id)
      .then((detail) => setRefreshedAccount(detail))
      .catch(() => {});
  }, [selectedAccount, fetchGlobalAccountDetail]);

  return (
    <>
      <Table
        emptyMessageTitle={t(`banking:connectedAccountDetails.globalAccounts.emptyMessageTitle`)}
        emptyMessageSubtitle={t(`banking:connectedAccountDetails.globalAccounts.emptyMessageSubtitle`)}
        onClickRow={(row: GlobalAccount) => {
          if (fetchGlobalAccountDetail) setSelectedAccount(row);
        }}
        columns={[
          {
            field: 'currency',
            headerName: t(`banking:connectedAccountDetails.globalAccounts.columns.currency.headerName`),
            renderCell: (globalAccount: GlobalAccount) => {
              const otherCurrencies = globalAccount.currencies.filter(
                (currency) => currency !== globalAccount.mainCurrency
              );
              return (
                <Box display="flex" gap="8px" alignItems="center">
                  <Typography>{globalAccount.mainCurrency}</Typography>
                  {otherCurrencies.length > 0 && (
                    <Chip
                      size="small"
                      label={`+${otherCurrencies.length}`}
                      tooltip={otherCurrencies.join(', ')}
                    />
                  )}
                </Box>
              );
            },
          },
          {
            field: 'countryCode',
            headerName: t(`banking:connectedAccountDetails.globalAccounts.columns.bankLocation.headerName`),
            renderCell: ({ countryCode }: GlobalAccount) => {
              return (
                <Typography>
                  {t(
                    `banking:connectedAccountDetails.globalAccounts.columns.bankLocation.countryCodes.${countryCode}`
                  )}
                </Typography>
              );
            },
          },
          {
            field: 'accountName',
            headerName: t(`banking:connectedAccountDetails.globalAccounts.columns.accountName.headerName`),
          },
          ...(showNickname
            ? [
                {
                  field: 'nickName',
                  headerName: t(`banking:connectedAccountDetails.globalAccounts.columns.nickName.headerName`),
                },
              ]
            : []),
          {
            field: 'accountNumber',
            headerName: t(`banking:connectedAccountDetails.globalAccounts.columns.accountNumber.headerName`),
          },
          {
            field: 'status',
            headerName: t(`banking:connectedAccountDetails.globalAccounts.columns.status.headerName`),
            renderCell: ({ status }: GlobalAccount) => {
              return (
                <Chip
                  size="small"
                  label={t(`banking:connectedAccounts.columns.status.values.${status}`)}
                  sx={{
                    ...(statusColorMap[status] || defaultStatusColor),
                  }}
                />
              );
            },
          },
        ]}
        data={globalAccounts}
      />
      {selectedAccount && fetchGlobalAccountDetail && (
        <InfoDrawer
          isSubmitting={isTogglingAccount}
          buttonLabel={t('banking:connectedAccountDetails.globalAccounts.close.button')}
          submit={
            showEnableButton && selectedAccount.status === GlobalAccountStatus.ACTIVE
              ? () => setShowCloseConfirm(true)
              : undefined
          }
          showDrawer={!!selectedAccount}
          handleClose={() => {
            setSelectedAccount(null);
            setRefreshedAccount(null);
          }}
        >
          <GlobalAccountDetails
            t={t}
            globalAccount={refreshedAccount ?? selectedAccount}
            marcoPayNumber={marcoPayNumber}
            accountId={accountId}
            showExportStatementButton
          />
        </InfoDrawer>
      )}
      {selectedAccount && (
        <ConfirmDialog
          open={showCloseConfirm}
          title={t('banking:connectedAccountDetails.globalAccounts.close.confirmTitle')}
          content={t('banking:connectedAccountDetails.globalAccounts.close.confirmContent')}
          color="secondary"
          isLoading={isTogglingAccount}
          onCancel={() => setShowCloseConfirm(false)}
          onConfirm={() => {
            setIsTogglingAccount(true);
            adminDisableGlobalAccount(accountId, selectedAccount.id)
              .then(() => {
                showSnackbar(t('banking:connectedAccountDetails.globalAccounts.close.success'), 'success');
                return refreshBankingData();
              })
              .then(() => {
                setShowCloseConfirm(false);
                setSelectedAccount(null);
              })
              .catch((error) => {
                showSnackbar(error.response.data.error_code, 'error');
              })
              .finally(() => {
                setIsTogglingAccount(false);
              });
          }}
        />
      )}
    </>
  );
};

export default GlobalAccountsTable;
